import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tag,
  message,
  Popconfirm,
  Image,
  Upload,
  Empty,
  Tabs,
  Row,
  Col,
  Cascader,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ProfileOutlined,
  SearchOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  type ServiceCategory,
  getAllServiceCategories,
  serviceSourceMap,
} from '../../services/serviceCategoryService';
import {
  type ServiceProduct,
  type ServiceProductRegion,
  type StaffCommissionType,
  createServiceProduct,
  deleteServiceProduct,
  getServiceProduct,
  getServiceProducts,
  serviceSourceOptions,
  staffCommissionTypeLabelMap,
  staffCommissionTypeOptions,
  updateServiceProduct,
} from '../../services/serviceProductService';
import {
  getRegionTree,
  type RegionItem,
} from '../../services/regionService';
import { normalizeFileUrl, uploadFile } from '../../services/fileService';
import RichTextEditor from '../../components/RichTextEditor';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

interface ServiceProductRegionForm extends ServiceProductRegion {
  cityId?: string;
  districtIds?: string[];
  regionPaths?: string[][];
}

const createEmptyServiceRegion = (): ServiceProductRegionForm => ({
  regionId: '',
  cityId: '',
  districtIds: [],
  regionPaths: [],
  provinceName: '',
  cityName: '',
  districtName: '',
});

const normalizeServiceRegion = (region?: Partial<ServiceProductRegion>): ServiceProductRegion => ({
  regionId: region?.regionId?.trim() || '',
  provinceName: region?.provinceName?.trim() || '',
  cityName: region?.cityName?.trim() || '',
  districtName: region?.districtName?.trim() || '',
});

const hasRegionContent = (region?: Partial<ServiceProductRegion>) =>
  Boolean(region?.regionId?.trim() || region?.provinceName?.trim() || region?.cityName?.trim() || region?.districtName?.trim());

const getFormServiceRegions = (regions?: ServiceProductRegion[]) => {
  const normalized = (regions || []).map(normalizeServiceRegion).filter(hasRegionContent);
  return normalized.length ? normalized : [createEmptyServiceRegion()];
};

const buildRegionPath = (region?: Partial<ServiceProductRegion>) => {
  const normalized = normalizeServiceRegion(region);
  if (normalized.provinceName && normalized.cityName && normalized.districtName) {
    return [normalized.provinceName, normalized.cityName, normalized.districtName];
  }
  return [];
};

const formatRegionText = (region?: Partial<ServiceProductRegion>) => {
  const normalized = normalizeServiceRegion(region);
  return [normalized.provinceName, normalized.cityName, normalized.districtName]
    .filter(Boolean)
    .join(' / ');
};

const formatCommissionText = (type?: StaffCommissionType, value?: number) => {
  const amount = Number(value || 0);
  if (type === 'payment_ratio') {
    return `${staffCommissionTypeLabelMap.payment_ratio} ${amount.toFixed(2)}%`;
  }
  return `${staffCommissionTypeLabelMap.fixed_amount} ¥${amount.toFixed(2)}`;
};

const convertRegionsToCascaderOptions = (regions: RegionItem[]): any[] => {
  return regions.map((region) => ({
    value: region.id,
    label: region.name,
    code: region.code,
    children: convertRegionsToCascaderOptions(region.children || []),
  }));
};

const buildRegionMetaMap = (regions: RegionItem[]) => {
  const metaMap = new Map<string, { idPath: string[]; namePath: string[] }>();

  const walk = (items: RegionItem[], parentIds: string[] = [], parentNames: string[] = []) => {
    items.forEach((item) => {
      const idPath = [...parentIds, item.id];
      const namePath = [...parentNames, item.name];
      metaMap.set(item.id, { idPath, namePath });
      if (item.code && item.code !== item.id) {
        metaMap.set(item.code, { idPath, namePath });
      }
      if (item.children?.length) {
        walk(item.children, idPath, namePath);
      }
    });
  };

  walk(regions);
  return metaMap;
};

const buildCityDistrictPathMap = (regions: RegionItem[]) => {
  const result = new Map<string, string[][]>();
  regions.forEach((province) => {
    const provinceDistrictPaths: string[][] = [];
    (province.children || []).forEach((city) => {
      const cityDistrictPaths = (city.children || []).map((district) => [province.id, city.id, district.id]);
      provinceDistrictPaths.push(...cityDistrictPaths);
      result.set(city.id, cityDistrictPaths);
      if (city.code && city.code !== city.id) {
        result.set(city.code, cityDistrictPaths);
      }
    });
    result.set(province.id, provinceDistrictPaths);
    if (province.code && province.code !== province.id) {
      result.set(province.code, provinceDistrictPaths);
    }
  });
  return result;
};

const groupServiceRegionsByCity = (
  serviceRegions: ServiceProductRegion[],
  regionMetaMap: Map<string, { idPath: string[]; namePath: string[] }>,
): ServiceProductRegionForm[] => {
  const grouped = new Map<string, ServiceProductRegionForm>();

  getFormServiceRegions(serviceRegions).forEach((region) => {
    const normalized = normalizeServiceRegion(region);
    const meta = normalized.regionId ? regionMetaMap.get(normalized.regionId) : undefined;
    const regionPath = meta?.idPath || [];
    const cityId = meta?.idPath[1] || '';
    const districtId = meta?.idPath[2] || normalized.regionId || '';
    const provinceName = meta?.namePath[0] || normalized.provinceName;
    const cityName = meta?.namePath[1] || normalized.cityName;
    const districtName = meta?.namePath[2] || normalized.districtName;
    const fallbackKey = [provinceName, cityName].filter(Boolean).join('/') || normalized.regionId || districtName;
    const key = cityId || fallbackKey;
    const current = grouped.get(key);

    if (current) {
      grouped.set(key, {
        ...current,
        districtIds: Array.from(new Set([...(current.districtIds || []), districtId].filter(Boolean))),
        regionPaths: regionPath.length === 3 ? [...(current.regionPaths || []), regionPath] : current.regionPaths || [],
      });
      return;
    }

    grouped.set(key, {
      ...normalized,
      cityId,
      districtIds: districtId ? [districtId] : [],
      regionPaths: regionPath.length === 3 ? [regionPath] : [],
      provinceName,
      cityName,
      districtName,
    });
  });

  return Array.from(grouped.values());
};


const ServiceProductPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ServiceProduct | null>(null);
  const [activeTabKey, setActiveTabKey] = useState('basic');
  const [searchForm] = Form.useForm();
  const [form] = Form.useForm();
  const tableWrapRef = useHorizontalTableWheel();

  const currentImageUrl = Form.useWatch('imageUrl', form);
  const currentCategoryId = Form.useWatch('categoryId', form);
  const currentCommissionType = Form.useWatch('staffCommissionType', form) as StaffCommissionType | undefined;
  const currentServiceRegions = Form.useWatch('serviceRegions', form) as ServiceProductRegionForm[] | undefined;

  const categoryOptions = useMemo(
    () => categories.map((item) => ({ label: item.name, value: item.id })),
    [categories],
  );

  const regionOptions = useMemo(() => convertRegionsToCascaderOptions(regions), [regions]);
  const regionMetaMap = useMemo(() => buildRegionMetaMap(regions), [regions]);
  const cityDistrictPathMap = useMemo(() => buildCityDistrictPathMap(regions), [regions]);

  const categoryMap = useMemo(
    () => new Map(categories.map((item) => [item.id, item])),
    [categories],
  );

  const selectedCategory = useMemo(() => {
    if (!currentCategoryId) {
      return null;
    }
    return categoryMap.get(currentCategoryId) || null;
  }, [categoryMap, currentCategoryId]);

  const currentSourceLabel = selectedCategory
    ? serviceSourceMap[selectedCategory.source] || selectedCategory.source
    : '';

  const loadRegionOptions = async () => {
    try {
      const res = await getRegionTree({ status: 1 });
      setRegions(res.data || []);
    } catch {
      message.error('加载地区选项失败');
    }
  };

  const tabOptions = [
    {
      key: 'basic',
      label: '基础信息',
      description: '维护分类、名称、图片、价格和状态等基础资料。',
      icon: <AppstoreOutlined />,
    },
    {
      key: 'other',
      label: '其他信息',
      description: '配置适用地区、服务人员抽成规则和未匹配城市金额。',
      icon: <AppstoreOutlined />,
    },
    {
      key: 'detail',
      label: '服务详情',
      description: '维护用户端展示的图文服务详情内容。',
      icon: <AppstoreOutlined />,
    },
  ] as const;

  const currentTab = tabOptions.find((item) => item.key === activeTabKey) || tabOptions[0];

  const loadProducts = async (page = pagination.page, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        ...searchForm.getFieldsValue(),
      };
      const res = await getServiceProducts(params);
      setProducts(res.data?.list || []);
      setPagination({
        page,
        pageSize,
        total: res.data?.total || 0,
      });
    } catch {
      message.error('加载服务产品失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const [categoryRes, productRes, regionRes] = await Promise.all([
          getAllServiceCategories(),
          getServiceProducts({ page: 1, pageSize: 10 }),
          getRegionTree({ status: 1 }),
        ]);
        setCategories(categoryRes.data || []);
        setProducts(productRes.data?.list || []);
        setRegions(regionRes.data || []);
        setPagination({
          page: 1,
          pageSize: 10,
          total: productRes.data?.total || 0,
        });
      } catch {
        message.error('初始化服务产品页面失败');
      } finally {
        setLoading(false);
      }
    };

    void initialize();
  }, []);

  const handleSearch = () => {
    loadProducts(1, pagination.pageSize);
  };

  const handleReset = () => {
    searchForm.resetFields();
    loadProducts(1, pagination.pageSize);
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setActiveTabKey('basic');
    form.resetFields();
    form.setFieldsValue({
      tags: [],
      serviceRegions: [createEmptyServiceRegion()],
      staffCommissionType: 'fixed_amount',
      staffCommissionValue: 0,
      unmatchedCityCommissionValue: 0,
      price: 0,
      originalPrice: 0,
      extraUnitPrice: 0,
      sort: 0,
      status: true,
      description: '',
      serviceDetail: '',
    });
    void loadRegionOptions();
    setModalVisible(true);
  };

  const handleEdit = async (record: ServiceProduct) => {
    setActiveTabKey('basic');
    let product = record;
    try {
      const productRes = await getServiceProduct(record.id);
      product = productRes.data || record;
    } catch {
      message.warning('获取服务项目详情失败，已使用列表数据回显');
    }
    setEditingProduct(product);
    let nextRegions = regions;
    if (!nextRegions.length) {
      try {
        const regionRes = await getRegionTree({ status: 1 });
        nextRegions = regionRes.data || [];
        setRegions(nextRegions);
      } catch {
        message.error('加载地区选项失败');
      }
    }
    const nextRegionMetaMap = buildRegionMetaMap(nextRegions);
    const normalizedRegions = getFormServiceRegions(product.serviceRegions).map((region) => {
      if (region.regionId) {
        return region;
      }
      const matchedRegionId = Array.from(nextRegionMetaMap.entries()).find(([, meta]) => {
        return meta.namePath[0] === region.provinceName && meta.namePath[1] === region.cityName && meta.namePath[2] === region.districtName;
      })?.[0];
      return {
        ...region,
        regionId: matchedRegionId || '',
      };
    });
    const formServiceRegions = groupServiceRegionsByCity(normalizedRegions, nextRegionMetaMap);

    form.setFieldsValue({
      ...product,
      tags: product.tags || [],
      serviceRegions: formServiceRegions,
      staffCommissionType: product.staffCommissionType || 'fixed_amount',
      staffCommissionValue: Number(product.staffCommissionValue || 0),
      unmatchedCityCommissionValue: Number(product.unmatchedCityCommissionValue || 0),
      price: Number(product.price || 0),
      originalPrice: Number(product.originalPrice || 0),
      extraUnitPrice: Number(product.extraUnitPrice || 0),
      description: product.description || '',
      serviceDetail: product.serviceDetail || '',
      status: product.status === 1,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteServiceProduct(id);
      message.success('删除成功');
      loadProducts();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '删除失败');
    }
  };

  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setImageUploading(true);
    try {
      const res = await uploadFile(file as File, 'service_product_image');
      form.setFieldsValue({ imageUrl: res.data.url });
      message.success('产品图片上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '产品图片上传失败');
      onError?.(error);
    } finally {
      setImageUploading(false);
    }
  };

  const handleRegionDistrictChange = (fieldName: number, selectedPaths: string[][]) => {
    const leafPathMap = new Map<string, string[]>();
    selectedPaths.forEach((path) => {
      if (path.length === 3) {
        leafPathMap.set(path.join('/'), path);
        return;
      }
      if (path.length === 1) {
        const provinceId = path[0];
        (cityDistrictPathMap.get(provinceId) || []).forEach((districtPath) => {
          leafPathMap.set(districtPath.join('/'), districtPath);
        });
      }
      if (path.length === 2) {
        const cityId = path[1];
        (cityDistrictPathMap.get(cityId) || []).forEach((districtPath) => {
          leafPathMap.set(districtPath.join('/'), districtPath);
        });
      }
    });
    const leafPaths = Array.from(leafPathMap.values());
    const districtIds = leafPaths.map((path) => path[path.length - 1]).filter(Boolean);
    const current = (form.getFieldValue(['serviceRegions', fieldName]) || {}) as ServiceProductRegionForm;
    const districtId = districtIds[0] || '';
    const districtMeta = districtId ? regionMetaMap.get(districtId) : undefined;

    form.setFieldValue(['serviceRegions', fieldName], {
      ...current,
      regionId: districtId,
      cityId: districtMeta?.idPath[1] || '',
      districtIds,
      regionPaths: leafPaths,
      provinceName: districtMeta?.namePath[0] || current.provinceName || '',
      cityName: districtMeta?.namePath[1] || current.cityName || '',
      districtName: districtMeta?.namePath[2] || current.districtName || '',
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const rawServiceRegions = (form.getFieldValue('serviceRegions') || []) as ServiceProductRegionForm[];
      const serviceRegions = rawServiceRegions
        .filter((item) => hasRegionContent(item) || Boolean(item.cityId) || Boolean(item.districtIds?.length) || Boolean(item.regionPaths?.length))
        .flatMap((item) => {
          const districtIdSet = new Set<string>();
          if (Array.isArray(item.regionPaths) && item.regionPaths.length > 0) {
            item.regionPaths.forEach((path) => {
              if (path.length === 3) {
                districtIdSet.add(path[path.length - 1]);
                return;
              }
              if (path.length === 1) {
                const provinceId = path[0];
                (cityDistrictPathMap.get(provinceId) || []).forEach((districtPath) => {
                  districtIdSet.add(districtPath[districtPath.length - 1]);
                });
              }
              if (path.length === 2) {
                const cityId = path[1];
                (cityDistrictPathMap.get(cityId) || []).forEach((districtPath) => {
                  districtIdSet.add(districtPath[districtPath.length - 1]);
                });
              }
            });
          } else if (Array.isArray(item.districtIds)) {
            item.districtIds.filter(Boolean).forEach((districtId) => districtIdSet.add(districtId));
          }
          const districtIds = Array.from(districtIdSet);
          if (districtIds.length > 0) {
            return districtIds.map((districtId) => {
              const regionPath = regionMetaMap.get(districtId)?.namePath || [];
              return {
                regionId: districtId,
                provinceName: regionPath[0] || item.provinceName,
                cityName: regionPath[1] || item.cityName,
                districtName: regionPath[2] || item.districtName,
              };
            });
          }

          const normalized = normalizeServiceRegion(item);
          const regionPath = normalized.regionId ? regionMetaMap.get(normalized.regionId)?.namePath || buildRegionPath(normalized) : buildRegionPath(normalized);
          return [{
            regionId: normalized.regionId,
            provinceName: regionPath[0] || normalized.provinceName,
            cityName: regionPath[1] || normalized.cityName,
            districtName: regionPath[2] || normalized.districtName,
          }];
        });

      setSubmitLoading(true);
      const payload = {
        ...values,
        description: values.description?.trim() || '',
        serviceDetail: values.serviceDetail?.trim() || '',
        tags: (values.tags || []).map((item: string) => item.trim()).filter(Boolean),
        serviceRegions,
        staffCommissionValue: Number(values.staffCommissionValue || 0),
        unmatchedCityCommissionValue: Number(values.unmatchedCityCommissionValue || 0),
        extraUnitPrice: Number(values.extraUnitPrice || 0),
        status: values.status ? 1 : 0,
      };
      delete (payload as any).cityId;
      delete (payload as any).districtIds;
      if (editingProduct) {
        await updateServiceProduct(editingProduct.id, payload);
        message.success('更新成功');
      } else {
        await createServiceProduct(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadProducts();
    } catch {
      // ignore
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns: ColumnsType<ServiceProduct> = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '产品说明',
      dataIndex: 'description',
      key: 'description',
      width: 260,
      ellipsis: true,
      render: (description?: string) => description || '-',
    },
    {
      title: '产品图片',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 120,
      render: (imageUrl: string) =>
        imageUrl ? (
          <Image
            src={normalizeFileUrl(imageUrl)}
            width={56}
            height={56}
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: '服务分类',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 160,
      render: (categoryName?: string) => categoryName || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source: string) => <Tag color="blue">{serviceSourceMap[source] || source}</Tag>,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 220,
      render: (tags: string[]) =>
        tags?.length ? (
          <Space size={[0, 4]} wrap>
            {tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: '适用地区',
      dataIndex: 'serviceRegions',
      key: 'serviceRegions',
      width: 280,
      render: (serviceRegions?: ServiceProductRegion[]) => {
        const regions = (serviceRegions || []).filter((item) => formatRegionText(item));
        if (!regions.length) {
          return '-';
        }
        return (
          <Space size={[0, 4]} wrap>
            {regions.slice(0, 2).map((region, index) => {
              const text = formatRegionText(region);
              return <Tag key={`${text}-${index}`}>{text}</Tag>;
            })}
            {regions.length > 2 ? <Tag color="default">等 {regions.length} 个地区</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: '服务人员抽成',
      key: 'staffCommission',
      width: 180,
      render: (_, record) => formatCommissionText(record.staffCommissionType, record.staffCommissionValue),
    },
    {
      title: '未匹配城市抽成',
      dataIndex: 'unmatchedCityCommissionValue',
      key: 'unmatchedCityCommissionValue',
      width: 180,
      render: (value: number, record) => formatCommissionText(record.staffCommissionType, value),
    },
    {
      title: '售价',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price: number) => `¥${Number(price || 0).toFixed(2)}`,
    },
    {
      title: '原价',
      dataIndex: 'originalPrice',
      key: 'originalPrice',
      width: 100,
      render: (price: number) => `¥${Number(price || 0).toFixed(2)}`,
    },
    {
      title: '超过数量1加价',
      dataIndex: 'extraUnitPrice',
      key: 'extraUnitPrice',
      width: 140,
      render: (price: number) => `¥${Number(price || 0).toFixed(2)}`,
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 90,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该服务产品吗？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <>
            <ProfileOutlined /> 服务产品
          </>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => loadProducts()}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增产品
            </Button>
          </Space>
        )}
      >
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="name" label="产品名称">
            <Input allowClear placeholder="请输入产品名称" style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="categoryId" label="服务分类">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={categoryOptions}
              placeholder="请选择服务分类"
              style={{ width: 220 }}
            />
          </Form.Item>
          <Form.Item name="source" label="来源">
            <Select
              allowClear
              options={serviceSourceOptions}
              placeholder="请选择来源"
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              allowClear
              placeholder="请选择状态"
              style={{ width: 140 }}
              options={[
                { label: '启用', value: 1 },
                { label: '禁用', value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={products}
            scroll={{ x: 1800 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => loadProducts(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Modal
        title={editingProduct ? '编辑服务产品' : '新增服务产品'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setActiveTabKey('basic');
        }}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        width={960}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            tags: [],
            serviceRegions: [createEmptyServiceRegion()],
            staffCommissionType: 'fixed_amount',
            staffCommissionValue: 0,
            unmatchedCityCommissionValue: 0,
            price: 0,
            originalPrice: 0,
            extraUnitPrice: 0,
            sort: 0,
            status: true,
            description: '',
            serviceDetail: '',
          }}
        >
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              borderRadius: 12,
              background: '#f5f8ff',
              border: '1px solid #d6e4ff',
            }}
          >
            <Space size={12} align="start">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#e6f4ff',
                  color: '#1677ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {currentTab.icon}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1f1f1f', marginBottom: 4 }}>
                  {currentTab.label}
                </div>
                <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.6 }}>{currentTab.description}</div>
              </div>
            </Space>
          </div>
          <Tabs
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            defaultActiveKey="basic"
            items={[
              {
                key: 'basic',
                label: (
                  <Space size={6}>
                    <AppstoreOutlined />
                    <span>基础信息</span>
                  </Space>
                ),
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="categoryId"
                        label="服务分类"
                        rules={[{ required: true, message: '请选择服务分类' }]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          options={categoryOptions}
                          placeholder="请选择服务分类"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="来源">
                        <Input value={currentSourceLabel} placeholder="选择分类后自动带出" readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label="产品名称"
                        rules={[{ required: true, message: '请输入产品名称' }]}
                      >
                        <Input placeholder="请输入产品名称" maxLength={100} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="tags" label="产品标签">
                        <Select
                          mode="tags"
                          tokenSeparators={[',']}
                          placeholder="输入标签后回车"
                          open={false}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name="description" label="产品说明">
                        <Input.TextArea
                          placeholder="请输入产品说明"
                          maxLength={1000}
                          rows={4}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="imageUrl"
                        label="产品图片"
                        rules={[{ required: true, message: '请上传产品图片' }]}
                      >
                        <Input placeholder="请上传产品图片" readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item>
                        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                          <Upload accept="image/*" showUploadList={false} customRequest={handleImageUpload}>
                            <Button loading={imageUploading} icon={<UploadOutlined />}>
                              上传产品图片
                            </Button>
                          </Upload>
                          {currentImageUrl ? (
                            <Image
                              src={normalizeFileUrl(currentImageUrl)}
                              alt="产品图片"
                              width={120}
                              style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                            />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无产品图片" />
                          )}
                        </Space>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="price"
                        label="售价"
                        rules={[{ required: true, message: '请输入售价' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入售价"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="originalPrice"
                        label="原价"
                        rules={[{ required: true, message: '请输入原价' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入原价"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="extraUnitPrice"
                        label="超过数量1加价"
                        rules={[{ required: true, message: '请输入超过数量1加价' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入超过数量1加价"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name="sort" label="排序">
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item name="status" label="状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'other',
                label: (
                  <Space size={6}>
                    <AppstoreOutlined />
                    <span>其他信息</span>
                  </Space>
                ),
                children: (
                  <Space orientation="vertical" size={24} style={{ width: '100%' }}>
                    <Form.Item
                      label="适用地区"
                      required
                      extra="可按省/市添加适用地区，并在当前市内多选区/县，多个地区共用同一套服务人员抽成规则。"
                      style={{ marginBottom: 0 }}
                    >
                      <Form.List
                        name="serviceRegions"
                        rules={[
                          {
                            validator: async (_, value: ServiceProductRegionForm[] | undefined) => {
                              const regions = (value || []).filter((item) => hasRegionContent(item) || Boolean(item.cityId) || Boolean(item.districtIds?.length) || Boolean(item.regionPaths?.length));
                              if (!regions.length) {
                                throw new Error('请至少添加一个适用地区');
                              }
                              if (regions.some((item: ServiceProductRegionForm) => !(item.regionPaths || []).length && !(item.districtIds || []).length)) {
                                throw new Error('请至少选择一个区 / 县');
                              }
                            },
                          },
                        ]}
                      >
                        {(fields, { add, remove }, { errors }) => (
                          <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                            {fields.map((field) => {
                              const regionValue = currentServiceRegions?.[field.name] as ServiceProductRegionForm | undefined;
                              const regionText = formatRegionText(regionValue);
                              const formRegionPaths = Array.isArray(regionValue?.regionPaths)
                                ? (regionValue?.regionPaths || []).filter((path) => path.length === 3)
                                : [];
                              const formDistrictIds = Array.isArray(regionValue?.districtIds) ? regionValue?.districtIds || [] : [];
                              const selectedRegionPaths = formRegionPaths.length > 0
                                ? formRegionPaths
                                : formDistrictIds
                                  .map((districtId) => regionMetaMap.get(districtId)?.idPath || [])
                                  .filter((path) => path.length === 3);
                              const hasSelectedRegions = selectedRegionPaths.length > 0;
                              const hasUnmatchedHistory = !hasSelectedRegions && !regionValue?.regionId && Boolean(regionText);

                              return (
                                <Row key={field.key} gutter={12} align="middle" wrap={false}>
                                  <Col flex="1">
                                    <Form.Item
                                      name={[field.name, 'regionPaths']}
                                      label={fields.length > 1 ? `地区 ${field.name + 1}` : undefined}
                                      required
                                      validateStatus={!hasSelectedRegions || hasUnmatchedHistory ? 'error' : undefined}
                                      help={!hasSelectedRegions ? '请至少选择一个区 / 县' : undefined}
                                      extra={hasUnmatchedHistory ? `历史地区：${regionText}，请重新选择后保存。` : undefined}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Cascader
                                        multiple
                                        maxTagCount="responsive"
                                        options={regionOptions}
                                        placeholder="请选择省 / 市 / 区，可多选"
                                        changeOnSelect
                                        showSearch={{
                                          filter: (inputValue, path) => {
                                            const keyword = inputValue.trim().toLowerCase();
                                            return path.some((option) => {
                                              const label = String(option.label || '').toLowerCase();
                                              const code = String((option as any).code || '').toLowerCase();
                                              return label.includes(keyword) || code.includes(keyword);
                                            });
                                          },
                                          render: (_, path) => path.map((option) => `${option.label}${(option as any).code ? ` (${(option as any).code})` : ''}`).join(' / '),
                                        }}
                                        onChange={(value) => handleRegionDistrictChange(field.name, value as string[][])}
                                        displayRender={(labels) => labels.join(' / ')}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col flex="140px">
                                    <Button
                                      block
                                      icon={<MinusCircleOutlined />}
                                      style={{ marginTop: fields.length > 1 ? 30 : 0 }}
                                      onClick={() => remove(field.name)}
                                      disabled={fields.length === 1}
                                    >
                                      删除
                                    </Button>
                                  </Col>
                                </Row>
                              );
                            })}
                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add(createEmptyServiceRegion())}>
                              新增适用地区
                            </Button>
                            <Form.ErrorList errors={errors} />
                          </Space>
                        )}
                      </Form.List>
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="staffCommissionType"
                          label="服务人员抽成方式"
                          rules={[{ required: true, message: '请选择服务人员抽成方式' }]}
                        >
                          <Select options={staffCommissionTypeOptions} placeholder="请选择服务人员抽成方式" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={currentCommissionType === 'payment_ratio' ? '服务人员抽成比例(%)' : '服务人员抽成金额(元)'}
                          required
                        >
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="staffCommissionValue" noStyle rules={[
                              { required: true, message: '请输入服务人员抽成值' },
                              {
                                validator: async (_, value) => {
                                  if (value === undefined || value === null || value === '') {
                                    throw new Error('请输入服务人员抽成值');
                                  }
                                  const numericValue = Number(value);
                                  if (Number.isNaN(numericValue)) {
                                    throw new Error('服务人员抽成值格式不正确');
                                  }
                                  if (currentCommissionType === 'payment_ratio') {
                                    if (numericValue <= 0 || numericValue > 100) {
                                      throw new Error('支付比例需大于 0 且不超过 100');
                                    }
                                    return;
                                  }
                                  if (numericValue < 0) {
                                    throw new Error('固定金额不能小于 0');
                                  }
                                },
                              },
                            ]}>
                              <InputNumber
                                min={0}
                                max={currentCommissionType === 'payment_ratio' ? 100 : undefined}
                                precision={2}
                                style={{ width: '100%' }}
                                placeholder={currentCommissionType === 'payment_ratio' ? '请输入支付比例，例如 15' : '请输入固定金额，例如 20'}
                              />
                            </Form.Item>
                            <Button disabled>{currentCommissionType === 'payment_ratio' ? '%' : '元'}</Button>
                          </Space.Compact>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={currentCommissionType === 'payment_ratio' ? '未匹配城市抽成比例(%)' : '未匹配城市抽成金额(元)'}
                          extra={currentCommissionType === 'payment_ratio' ? '当订单地址未命中已配置的省/市/区时，按该支付比例计算抽成。' : '当订单地址未命中已配置的省/市/区时，使用该固定金额。'}
                          required
                        >
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="unmatchedCityCommissionValue" noStyle rules={[
                              { required: true, message: '请输入未匹配城市抽成值' },
                              {
                                validator: async (_, value) => {
                                  if (value === undefined || value === null || value === '') {
                                    throw new Error('请输入未匹配城市抽成值');
                                  }
                                  const numericValue = Number(value);
                                  if (Number.isNaN(numericValue)) {
                                    throw new Error('未匹配城市抽成值格式不正确');
                                  }
                                  if (currentCommissionType === 'payment_ratio') {
                                    if (numericValue <= 0 || numericValue > 100) {
                                      throw new Error('未匹配城市支付比例需大于 0 且不超过 100');
                                    }
                                    return;
                                  }
                                  if (numericValue < 0) {
                                    throw new Error('未匹配城市固定金额不能小于 0');
                                  }
                                },
                              },
                            ]}>
                              <InputNumber
                                min={0}
                                max={currentCommissionType === 'payment_ratio' ? 100 : undefined}
                                precision={2}
                                style={{ width: '100%' }}
                                placeholder={currentCommissionType === 'payment_ratio' ? '请输入未匹配城市支付比例，例如 15' : '请输入未匹配城市固定金额，例如 30'}
                              />
                            </Form.Item>
                            <Button disabled>{currentCommissionType === 'payment_ratio' ? '%' : '元'}</Button>
                          </Space.Compact>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Space>
                ),
              },
              {
                key: 'detail',
                label: (
                  <Space size={6}>
                    <AppstoreOutlined />
                    <span>服务详情</span>
                  </Space>
                ),
                children: (
                  <Form.Item
                    name="serviceDetail"
                    label="服务详情"
                    extra="用于维护产品详情图文内容，可上传图片。"
                  >
                    <RichTextEditor placeholder="请输入服务详情内容" height={420} />
                  </Form.Item>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceProductPage;
